import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api, DfeDefinitionSummary } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { AlertTriangle, Check, ChevronsUpDown, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  buildBasePetMetadata,
  findBasePetSpeciesMismatch,
  formatBasePetMetadata,
  isBasePetDefinitionName,
} from '../base-pet-metadata';
import { formatLitPetMetadata, isLitPetDefinitionName } from '../litpet-metadata';

interface MintItemDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: {
    itemDefId: string;
    toUsername: string;
    quantity: number;
    metadata?: Record<string, unknown>;
    notes?: string;
    category?: string;
    position?: string;
  }) => Promise<void>;
}

export function MintItemDialog({ open, onClose, onConfirm }: MintItemDialogProps) {
  // The whole definition, not just its id: the search box refetches the list as
  // the admin types, so a selection looked up out of `definitions` would vanish
  // the moment the current results no longer contain it.
  const [selectedDefinition, setSelectedDefinition] = useState<DfeDefinitionSummary | null>(null);
  const [toUsername, setToUsername] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState('satchel');
  const [position, setPosition] = useState('');
  const [metadataJson, setMetadataJson] = useState('');
  const [maxStats, setMaxStats] = useState(false);
  /** Set once the admin types in the metadata box, so a prefill never overwrites their work. */
  const [metadataEdited, setMetadataEdited] = useState(false);
  /** A LitPet template is generated server-side, so the prefill is async. */
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  /**
   * Guards against a slow template response landing after the admin has moved
   * on to a different item — only the newest request may write the box.
   */
  const templateRequestRef = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const itemDefId = selectedDefinition?.itemDefId ?? '';
  const isBasePet = isBasePetDefinitionName(selectedDefinition?.name);
  const isLitPet = isLitPetDefinitionName(selectedDefinition?.name);

  const [definitions, setDefinitions] = useState<DfeDefinitionSummary[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loadingDefs, setLoadingDefs] = useState(false);

  const fetchDefinitions = useCallback(async (search: string) => {
    setLoadingDefs(true);
    try {
      const result = await api.listDfeDefinitions({ search: search || undefined, limit: 50 });
      setDefinitions(result.definitions);
    } catch {
      // Definitions will just be empty; user can retry by typing again
    } finally {
      setLoadingDefs(false);
    }
  }, []);

  // Load initial definitions when dialog opens; clean up timeout on unmount
  useEffect(() => {
    if (!open) return;
    fetchDefinitions('');
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [open, fetchDefinitions]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchDefinitions(value);
    }, 300);
  };

  const sortedDefinitions = useMemo(
    () => [...definitions].sort((a, b) => a.name.localeCompare(b.name)),
    [definitions],
  );

  const selectedName = selectedDefinition?.name;

  /**
   * Fill the metadata box from the Base Pet template, or clear it for anything
   * that has no template. Clears the edited flag — the box now holds generated
   * content again, so a later prefill is free to replace it.
   */
  const applyMetadataTemplate = useCallback(
    (definition: DfeDefinitionSummary | null, maxed: boolean) => {
      // Every path below supersedes any in-flight LitPet request.
      const requestId = ++templateRequestRef.current;
      setMetadataEdited(false);

      if (definition && isBasePetDefinitionName(definition.name)) {
        setGeneratingTemplate(false);
        setMetadataJson(formatBasePetMetadata(buildBasePetMetadata(definition.name, { maxed })));
        return;
      }

      if (definition && isLitPetDefinitionName(definition.name)) {
        // Generated server-side: the genome rules and the stat/rank/release
        // rolls live with the crafting code, so the template is produced by the
        // same generator rather than a client-side copy of it.
        setGeneratingTemplate(true);
        setMetadataJson('');
        void api
          .generateDfeLitpetTemplate(definition.name)
          .then((result) => {
            if (templateRequestRef.current !== requestId) return;
            setMetadataJson(formatLitPetMetadata(result.metadata));
          })
          .catch(() => {
            if (templateRequestRef.current !== requestId) return;
            // Leave the box empty and let the admin type: a failed convenience
            // must not block the mint.
            setMetadataJson('');
          })
          .finally(() => {
            if (templateRequestRef.current !== requestId) return;
            setGeneratingTemplate(false);
          });
        return;
      }

      setGeneratingTemplate(false);
      setMetadataJson('');
    },
    [],
  );

  const handleSelectDefinition = useCallback(
    (definition: DfeDefinitionSummary) => {
      setSelectedDefinition(definition);
      setComboboxOpen(false);
      // Hand-typed metadata is the admin's own work — a selection change must
      // never destroy it. "Reset to template" is the way back.
      if (metadataEdited) return;
      setMaxStats(false);
      applyMetadataTemplate(definition, false);
    },
    [applyMetadataTemplate, metadataEdited],
  );

  // Flipping the switch is an explicit instruction, so unlike a selection
  // change it overrides the hand-edit guard.
  const handleMaxStatsChange = useCallback(
    (checked: boolean) => {
      setMaxStats(checked);
      applyMetadataTemplate(selectedDefinition, checked);
    },
    [applyMetadataTemplate, selectedDefinition],
  );

  const handleResetMetadata = useCallback(() => {
    applyMetadataTemplate(selectedDefinition, maxStats);
  }, [applyMetadataTemplate, selectedDefinition, maxStats]);

  const handleMetadataChange = useCallback((value: string) => {
    // Typing supersedes any in-flight LitPet template: without this, a slow
    // response would overwrite the admin's work when it lands, and a failed one
    // would clear it.
    templateRequestRef.current += 1;
    setGeneratingTemplate(false);
    setMetadataJson(value);
    setMetadataEdited(true);
  }, []);

  /**
   * Because a hand-edited blob deliberately survives a definition change, it
   * can end up describing a different pet than the one being minted. Nothing
   * downstream catches that, so surface it here.
   */
  const speciesMismatch = useMemo(() => {
    if (!selectedDefinition || !isBasePet) return null;
    const trimmed = metadataJson.trim();
    if (!trimmed) return null;
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
      return findBasePetSpeciesMismatch(selectedDefinition.name, parsed as Record<string, unknown>);
    } catch {
      // Invalid JSON is already reported on submit; don't double-report here.
      return null;
    }
  }, [selectedDefinition, isBasePet, metadataJson]);

  const handleSubmit = async () => {
    if (!itemDefId) {
      setError('Item Name is required');
      return;
    }
    if (!toUsername.trim()) {
      setError('Target Username is required');
      return;
    }
    const qty = parseInt(quantity, 10);
    if (!qty || qty < 1) {
      setError('Quantity must be at least 1');
      return;
    }

    let metadata: Record<string, unknown> | undefined;
    const trimmedMetadata = metadataJson.trim();
    if (trimmedMetadata) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(trimmedMetadata);
      } catch {
        setError('Invalid metadata JSON');
        return;
      }
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setError('Metadata must be a JSON object');
        return;
      }
      // Mirror the backend's 10KB cap so admins get fast feedback instead of
      // a generic server error after a network round trip.
      if (JSON.stringify(parsed).length > 10240) {
        setError('Metadata must be 10KB or less');
        return;
      }
      metadata = parsed as Record<string, unknown>;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({
        itemDefId,
        toUsername: toUsername.trim(),
        quantity: qty,
        notes: notes.trim() || undefined,
        category: category.trim() || undefined,
        position: position.trim() || undefined,
        ...(metadata !== undefined ? { metadata } : {}),
      });
      resetForm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mint failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedDefinition(null);
    setToUsername('');
    setQuantity('1');
    setNotes('');
    setCategory('satchel');
    setPosition('');
    setMetadataJson('');
    setMaxStats(false);
    setMetadataEdited(false);
    setSearchQuery('');
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mint Items</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Item Name</Label>
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="w-full justify-between font-normal"
                >
                  {loadingDefs ? 'Loading items...' : selectedName || 'Search items...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Type to search..."
                    value={searchQuery}
                    onValueChange={handleSearchChange}
                  />
                  <CommandList>
                    <CommandEmpty>{loadingDefs ? 'Searching...' : 'No items found.'}</CommandEmpty>
                    <CommandGroup>
                      {sortedDefinitions.map((def) => (
                        <CommandItem
                          key={def.itemDefId}
                          value={def.itemDefId}
                          onSelect={() => handleSelectDefinition(def)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              itemDefId === def.itemDefId ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {def.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label htmlFor="mintToUser">Target Username</Label>
            <Input
              id="mintToUser"
              value={toUsername}
              onChange={(e) => setToUsername(e.target.value)}
              placeholder="username"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mintQty">Quantity</Label>
            <Input
              id="mintQty"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mintNotes">Notes (optional)</Label>
            <Textarea
              id="mintNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="mintCategory">Category</Label>
              <Input
                id="mintCategory"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mintPosition">Position</Label>
              <Input
                id="mintPosition"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>
          {isBasePet && (
            <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="mintMaxStats" className="font-normal">
                  Max stats
                </Label>
                <Switch
                  id="mintMaxStats"
                  checked={maxStats}
                  onCheckedChange={handleMaxStatsChange}
                  aria-describedby="mintMaxStatsHelp"
                />
              </div>
              <p id="mintMaxStatsHelp" className="text-xs text-muted-foreground">
                {maxStats
                  ? 'Fully improved: every stat and improvement at its cap, so the pet passes the LitPet breeding gate.'
                  : 'Freshly crafted: lowest magical power, no improvements spent.'}{' '}
                Values match what the Crafting arche mints. The JSON below stays editable.
              </p>
              {speciesMismatch && (
                <p className="flex items-start gap-1.5 text-xs font-medium text-destructive">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    This metadata describes <strong>{speciesMismatch.actual}</strong>, but you are
                    minting <strong>{speciesMismatch.expected}</strong>. Minting it will create a{' '}
                    {speciesMismatch.expected} carrying {speciesMismatch.actual}&apos;s stats and
                    breeding genes.
                  </span>
                </p>
              )}
              {(metadataEdited || speciesMismatch) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={handleResetMetadata}
                >
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Reset to template
                </Button>
              )}
            </div>
          )}
          {isLitPet && (
            <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
              <p className="text-xs text-muted-foreground">
                {generatingTemplate
                  ? 'Generating a random LitPet…'
                  : metadataJson
                    ? 'Randomly generated with the same rules the Crafting arche uses to breed a LitPet: power rolled from the 1000-Heptal curve (usually low \u2014 7 is rare), Normal rank, a release from a random magic-tile level, a random heptagen point resolved to a branch and ability, and this species\u2019 own genes. Every field below stays editable.'
                    : 'No template generated. You can type metadata by hand, or pick the item again to retry.'}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleResetMetadata}
                disabled={generatingTemplate}
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                {metadataEdited ? 'Reset to template' : 'Reroll'}
              </Button>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="mintMetadata">Metadata (JSON, optional)</Label>
            <Textarea
              id="mintMetadata"
              value={metadataJson}
              onChange={(e) => handleMetadataChange(e.target.value)}
              rows={isBasePet || isLitPet ? 12 : 4}
              className="font-mono text-xs"
              placeholder='{ "power": 5, "rarity": "rare" }'
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          {/*
            Blocked while a template is generating: the box is deliberately empty
            until it lands, and the mint endpoint accepts omitted metadata — so
            submitting early would silently create a LitPet with none.
          */}
          <Button onClick={handleSubmit} disabled={submitting || generatingTemplate}>
            {submitting ? 'Minting...' : 'Mint'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
